/**
 * Routes API Service
 */

import api from './api';
import type {
  ApiResponse,
  RouteRun,
  Route,
  RouteStop,
  RouteOptimizationResult,
  PublishRoutesResult,
  RoutePreviewResult,
} from '../types';

export interface PreviewRoutesParams {
  taskUids: string[];
  routeDate?: string;
  startLocationMode?: 'hub_start' | 'free_start';
  depotLocation?: { lat: number; lng: number };
  startTime?: string; // HH:MM format
  endTime?: string; // HH:MM format
  secondsPerParcel?: number; // seconds per parcel/order (default: 5)
  baseServiceTimeMin?: number; // base service time in minutes for parking/walking (default: 5)
  maxRoutes?: number; // Max number of routes to create (auto if not specified)
  optimizationGoal?: 'balanced' | 'minimize_time' | 'minimize_vehicles'; // How to optimize routes
  speedFactor?: number; // Vehicle speed multiplier (1.0 = 100% speed, 0.5 = 50% speed)
  useMerchantTimeWindows?: boolean; // Whether to consider merchant preferred pickup time windows (default: true)
  vehicleCapacity?: number; // Vehicle capacity (default: 300)
  routingMode?: 'pickup_only' | 'rto_only' | 'mixed' | 'all'; // Which task types to route (server default: pickup_only; 'all' includes cash)
}

/**
 * Preview routes - calls Verso to optimize but does NOT save
 * This is a synchronous call that returns immediately
 */
export async function previewRoutes(params: PreviewRoutesParams): Promise<RoutePreviewResult> {
  const response = await api.post<ApiResponse<RoutePreviewResult>>('/routes/preview', params);

  if (!response.data.success || !response.data.data) {
    // The engine's explanatory error (e.g. "excluded by routing mode") lives on
    // the result payload; the top-level error is only set by request validation.
    throw new Error(
      response.data.data?.error || response.data.error || 'Failed to preview routes'
    );
  }

  return response.data.data;
}

export interface OptimizeRoutesParams {
  cityId: number;
  date?: string;
  slot: string;
  routingMode?: 'pickup_only' | 'rto_only' | 'mixed' | 'all';
  startLocationMode?: 'hub_start' | 'free_start';
  regionId?: number;
  zoneId?: string;
  statusSlugs?: string[];
  excludeAssigned?: boolean;
  trafficModel?: 'best_guess' | 'pessimistic' | 'optimistic';
}

/**
 * Create route run and optimize routes
 */
export async function optimizeRoutes(params: OptimizeRoutesParams): Promise<RouteOptimizationResult> {
  const response = await api.post<ApiResponse<RouteOptimizationResult>>('/routes/optimize', params);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to optimize routes');
  }
  
  return response.data.data;
}

/**
 * Get route runs for a city and date
 */
export async function getRouteRuns(cityId: number, date?: string): Promise<RouteRun[]> {
  const queryParams = new URLSearchParams();
  queryParams.set('cityId', cityId.toString());
  if (date) queryParams.set('date', date);

  const response = await api.get<ApiResponse<RouteRun[]>>(
    `/routes/runs?${queryParams.toString()}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch route runs');
  }
  
  return response.data.data;
}

/**
 * Get all routes for a city and date without run filtering
 */
export async function getRoutesByDate(cityId: number, date?: string): Promise<Route[]> {
  const queryParams = new URLSearchParams();
  queryParams.set('cityId', cityId.toString());
  if (date) queryParams.set('date', date);

  const response = await api.get<ApiResponse<Route[]>>(
    `/routes?${queryParams.toString()}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch routes');
  }
  
  return response.data.data;
}

/**
 * Get a specific route run with its routes
 */
export async function getRouteRun(runId: string): Promise<{ routeRun: RouteRun; routes: Route[] }> {
  const response = await api.get<ApiResponse<{ routeRun: RouteRun; routes: Route[] }>>(
    `/routes/runs/${runId}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch route run');
  }
  
  return response.data.data;
}

/**
 * Get a specific route with its stops
 */
export async function getRoute(routeId: string): Promise<{ route: Route; stops: RouteStop[] }> {
  const response = await api.get<ApiResponse<{ route: Route; stops: RouteStop[] }>>(
    `/routes/${routeId}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch route');
  }
  
  return response.data.data;
}

/**
 * Publish routes - assign tasks to drivers
 */
export async function publishRoutes(runId: string): Promise<PublishRoutesResult> {
  const response = await api.post<ApiResponse<PublishRoutesResult>>(
    `/routes/runs/${runId}/publish`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to publish routes');
  }
  
  return response.data.data;
}

/**
 * Reassign a task to another route
 */
export async function reassignTask(
  taskUid: string,
  toRouteId: string,
  insertAtSequence?: number
): Promise<{ success: boolean; error?: string }> {
  const response = await api.post<ApiResponse<{ success: boolean; error?: string }>>(
    '/routes/reassign',
    { taskUid, toRouteId, insertAtSequence }
  );
  
  return response.data.data || { success: response.data.success, error: response.data.error };
}

/**
 * Reorder stops in a route
 */
export async function reorderStops(
  routeId: string,
  newOrder: string[]
): Promise<{ success: boolean; error?: string }> {
  const response = await api.post<ApiResponse<{ success: boolean; error?: string }>>(
    `/routes/${routeId}/reorder`,
    { newOrder }
  );
  
  return response.data.data || { success: response.data.success, error: response.data.error };
}

/**
 * Get stop by task UID
 */
export async function getStopByTaskUid(taskUid: string): Promise<RouteStop> {
  const response = await api.get<ApiResponse<RouteStop>>(`/routes/stops/${taskUid}`);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch stop');
  }
  
  return response.data.data;
}

// ============================================================================
// Route Creation and Driver Assignment (New Flow)
// ============================================================================

export interface CreateRoutesParams {
  routes: Array<{
    driverUid?: string;
    driverName?: string;
    polyline?: string; // Encoded polyline from preview
    stops: Array<{
      taskUid: string;
      taskType?: 'pickup' | 'rto' | 'cash'; // Task type for RTO/cash differentiation
      lat: number;
      lng: number;
      platformCode?: string;
      merchantName?: string;
      areaName?: string;
      numberOfOrders?: number;
      arrivalTime?: string;
      googleArrivalTime?: string;
      serviceTimeMin?: number;
    }>;
    totalDistanceKm: number;
    totalDurationMin: number;
    googleTotalDurationMin?: number;
    totalOrders: number;
  }>;
  routeDate?: string;
  cityId?: number;
  slot?: string;
  mapBounds?: string | null; // Map bounds for preserving view
  mapZoom?: number; // Map zoom level
}

export interface CreateRoutesResult {
  routeRunId: string;
  routes: Array<{
    routeId: string;
    routeName: string;
    stopCount: number;
  }>;
  totalRoutes: number;
}

/**
 * Create routes from preview - saves routes to database
 * Routes are created WITHOUT drivers assigned (drivers assigned later on Routes page)
 */
export async function createRoutes(params: CreateRoutesParams): Promise<CreateRoutesResult> {
  const response = await api.post<ApiResponse<CreateRoutesResult>>('/routes/create', params);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to create routes');
  }
  
  return response.data.data;
}

export interface CoreApiTaskResult {
  taskUid: string;
  success: boolean;
  error?: string;
  priority?: number; // Sequence/priority for retry
}

export interface CoreApiStatus {
  success: boolean;
  assignedTasks?: number;
  unassignedTasks?: number;
  failedTasks: number;
  failedAssignments?: CoreApiTaskResult[];
  failedUnassignments?: CoreApiTaskResult[];
}

export interface AssignDriverResult {
  routeId: string;
  driverUid: string;
  driverName: string;
  message: string;
  coreApiStatus?: CoreApiStatus;
}

export interface UnassignDriverResult {
  routeId: string;
  message: string;
  coreApiStatus?: CoreApiStatus;
}

/**
 * Assign a driver to an existing route
 * Checks driver schedule before assignment
 */
export async function assignDriverToRoute(
  routeId: string,
  driverUid: string
): Promise<AssignDriverResult> {
  const response = await api.post<ApiResponse<AssignDriverResult>>(
    `/routes/${routeId}/assign-driver`,
    { driverUid }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to assign driver');
  }
  
  return response.data.data;
}

export interface DriverRecommendationCandidate {
  driver_uid: string;
  driver_name: string;
  distance_km: number | null;
  distance_from: 'live_position' | 'start_location' | 'depot' | null;
  shift_ok: boolean;
  shift_window: { start: string; end: string };
  capacity: number;
  demand: number;
  capacity_ok: boolean;
  /**
   * Whether this driver's van can take the route's cold-chain cargo. Always
   * true for a route that isn't cold, and true for everyone while no
   * refrigerated van is flagged in Schedules — matching the assign-driver
   * guard, so the dropdown never offers a driver the guard would refuse.
   */
  cold_ok: boolean;
  plan_fit: boolean | null;
  recommended: boolean;
}

export interface DriverRecommendations {
  route_id: string;
  zone_id: string | null;
  candidates: DriverRecommendationCandidate[];
  recommended_driver_uid: string | null;
  warnings: string[];
}

/**
 * Ranked driver candidates for a route (zone pool, flying distance,
 * capacity/shift gates, Verso fit-check). Advisory only — the officer picks.
 */
export async function getDriverRecommendations(routeId: string): Promise<DriverRecommendations> {
  const response = await api.get<ApiResponse<DriverRecommendations>>(
    `/routes/${routeId}/driver-recommendations`
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to load driver recommendations');
  }
  return response.data.data;
}

/**
 * Remove driver assignment from a route
 */
export async function unassignDriverFromRoute(
  routeId: string
): Promise<UnassignDriverResult> {
  const response = await api.post<ApiResponse<UnassignDriverResult>>(
    `/routes/${routeId}/unassign-driver`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to unassign driver');
  }
  
  return response.data.data;
}

export interface ProgressEvent {
  current: number;
  total: number;
  taskUid: string;
  success: boolean;
  error?: string;
  successCount: number;
  failedCount: number;
}

export interface StreamCompleteEvent {
  success: boolean;
  routeId: string;
  driverUid?: string;
  driverName?: string;
  coreApiStatus: CoreApiStatus;
  message: string;
}

/**
 * Assign a driver to a route (bulk operation - no streaming)
 */
export async function assignDriverToRouteWithProgress(
  routeId: string,
  driverUid: string,
  callbacks: {
    onStart?: (data: { routeId: string; driverUid: string; driverName: string; totalTasks: number; operation: string }) => void;
    onProgress?: (data: ProgressEvent) => void;
    onComplete?: (data: StreamCompleteEvent) => void;
    onError?: (error: string) => void;
  }
): Promise<() => void> {
  try {
    // Call the bulk assign endpoint
    const response = await api.post<ApiResponse<AssignDriverResult>>(
      `/routes/${routeId}/assign-driver`,
      { driverUid }
    );

    if (response.data.success) {
      // Simulate complete event for compatibility
      callbacks.onComplete?.({
        success: true,
        routeId,
        driverUid,
        driverName: response.data.data?.driverName || '',
        coreApiStatus: response.data.data?.coreApiStatus || { success: true, assignedTasks: 0, failedTasks: 0 },
        message: 'Driver assigned successfully',
      });
    } else {
      callbacks.onError?.(response.data.error || 'Failed to assign driver');
    }
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
    callbacks.onError?.(axiosError.response?.data?.error || axiosError.message || 'Failed to assign driver');
  }

  // Return no-op abort function for compatibility
  return () => {};
}

/**
 * Unassign a driver from a route (bulk operation - no streaming)
 */
export async function unassignDriverFromRouteWithProgress(
  routeId: string,
  callbacks: {
    onStart?: (data: { routeId: string; driverUid: string; driverName: string; totalTasks: number; operation: string }) => void;
    onProgress?: (data: ProgressEvent) => void;
    onComplete?: (data: StreamCompleteEvent) => void;
    onError?: (error: string) => void;
  }
): Promise<() => void> {
  try {
    // Call the bulk unassign endpoint
    const response = await api.post<ApiResponse<UnassignDriverResult>>(
      `/routes/${routeId}/unassign-driver`,
      {}
    );

    if (response.data.success) {
      // Simulate complete event for compatibility
      callbacks.onComplete?.({
        success: true,
        routeId,
        coreApiStatus: response.data.data?.coreApiStatus || { success: true, unassignedTasks: 0, failedTasks: 0 },
        message: 'Driver unassigned successfully',
      });
    } else {
      callbacks.onError?.(response.data.error || 'Failed to unassign driver');
    }
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
    callbacks.onError?.(axiosError.response?.data?.error || axiosError.message || 'Failed to unassign driver');
  }

  // Return no-op abort function for compatibility
  return () => {};
}

/**
 * Retry assigning a single task to a driver
 * @param taskUid - The task UID to assign
 * @param driverUid - The driver UID to assign to
 * @param priority - The priority/sequence of the task (optional)
 */
export async function retryTaskAssignment(
  taskUid: string,
  driverUid: string,
  priority?: number
): Promise<{ success: boolean; error?: string }> {
  const response = await api.post<ApiResponse<{ success: boolean; error?: string }>>(
    `/routes/tasks/${taskUid}/assign`,
    { driverUid, priority }
  );
  
  return response.data.data || { success: response.data.success, error: response.data.error };
}

/**
 * Retry unassigning a single task
 */
export async function retryTaskUnassignment(
  taskUid: string,
  driverUid?: string
): Promise<{ success: boolean; error?: string }> {
  const response = await api.post<ApiResponse<{ success: boolean; error?: string }>>(
    `/routes/tasks/${taskUid}/unassign`,
    { driverUid }
  );

  return response.data.data || { success: response.data.success, error: response.data.error };
}

/**
 * Fully unassign ("release") a task from its active route: drops the driver and removes
 * the task from the route so it returns to the unassigned pool and can be re-planned.
 * Powers the "Unassign" button on the Tasks page.
 */
export async function releaseTaskFromRoute(taskUid: string): Promise<void> {
  const response = await api.post<ApiResponse<unknown>>(
    `/routes/tasks/${taskUid}/release`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to unassign task');
  }
}

/**
 * Delete a route (only if not started)
 */
export async function deleteRoute(routeId: string): Promise<{ message: string }> {
  try {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `/routes/${routeId}`
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete route');
    }
    
    return response.data.data || { message: 'Route deleted' };
  } catch (error: unknown) {
    // Extract the actual error message from the backend
    const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
    const errorMessage = axiosError.response?.data?.error || axiosError.message || 'Failed to delete route';
    throw new Error(errorMessage);
  }
}

/**
 * Remove a stop from a route
 */
export async function removeStopFromRoute(
  routeId: string,
  stopId: string
): Promise<{ message: string; taskUid: string; newStopCount: number }> {
  const response = await api.delete<ApiResponse<{ message: string; taskUid: string; newStopCount: number }>>(
    `/routes/${routeId}/stops/${stopId}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to remove stop');
  }
  
  return response.data.data;
}

export interface SplitRouteResult {
  newRouteId: string;
  newRouteName: string;
  movedTaskUids: string[];
  coreSynced: boolean;
  /** Core released the tasks but refused the new driver — the split stands, the assignment doesn't. */
  assignWarning?: string;
}

/**
 * Split selected stops onto a new sibling route, optionally for another PA.
 * The sibling joins the same run, so it keeps the zone and SHIFT identity.
 */
export async function splitRoute(
  routeId: string,
  stopIds: string[],
  driver?: { uid: string; name?: string },
  routeName?: string
): Promise<SplitRouteResult> {
  const response = await api.post<ApiResponse<SplitRouteResult>>(`/routes/${routeId}/split`, {
    stopIds,
    ...(driver ? { driverUid: driver.uid, driverName: driver.name } : {}),
    ...(routeName ? { routeName } : {}),
  });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to split route');
  }

  return response.data.data;
}

/**
 * Reorder stops in a route
 */
export async function reorderRouteStops(
  routeId: string,
  newOrder: string[]
): Promise<{ success: boolean }> {
  const response = await api.post<ApiResponse<{ success: boolean }>>(
    `/routes/${routeId}/reorder`,
    { newOrder }
  );
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to reorder stops');
  }
  
  return response.data.data || { success: true };
}

/**
 * Add a stop to an existing route and re-sequence via Verso
 */
/**
 * `acknowledgeCold` carries the officer's answer to the refrigerated-van
 * warning. Without it the server refuses a cold stop on a warm van with
 * `code: 'COLD_DRIVER_MISMATCH'`; with it, the add goes through and is logged
 * against the user.
 */
export async function addStopToRoute(
  routeId: string,
  taskUid: string,
  acknowledgeCold = false
): Promise<{ message: string; routeId: string; newStopCount: number; totalDistanceKm: number; totalDurationMin: number }> {
  const response = await api.post<ApiResponse<{ message: string; routeId: string; newStopCount: number; totalDistanceKm: number; totalDurationMin: number }>>(
    `/routes/${routeId}/stops`,
    { taskUid, acknowledgeCold }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to add stop to route');
  }
  
  return response.data.data;
}

export interface RouteStopWithStatus extends RouteStop {
  task_status?: string;
  task_id?: number;
}

export interface SyncStatusResult {
  route: Route;
  stops: RouteStopWithStatus[];
  progress: {
    completed: number;
    total: number;
    cancelled: number;
    stopped: number;
    inProgress: number;
    hasWarning: boolean;
  };
  statusChanged: boolean;
  oldStatus: string;
  newStatus: string;
}

/**
 * Sync task statuses from Core DB and update route status
 */
export async function syncRouteStatus(routeId: string): Promise<SyncStatusResult> {
  const response = await api.post<ApiResponse<SyncStatusResult>>(
    `/routes/${routeId}/sync-status`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to sync route status');
  }
  
  return response.data.data;
}

// ============================================================================
// Task Timing - Actual vs Estimated
// ============================================================================

export interface TaskActualTiming {
  assigned_at: string | null;
  driving_at: string | null;
  arrived_at: string | null;
  in_progress_at: string | null;
  done_at: string | null;
  cancelled_at: string | null;
  driving_duration_min: number | null;
  service_duration_min: number | null;
  total_duration_min: number | null;
  waiting_before_drive_min: number | null;
  waiting_at_location_min: number | null;
}

export interface TaskEstimatedTiming {
  arrival_time: string | null;
  service_time_min: number;
  total_time_min: number;
}

export interface TaskTimingComparison {
  service_variance_min: number | null;
  service_variance_percent: number | null;
  total_variance_min: number | null;
  total_variance_percent: number | null;
}

export interface TaskTimingDetail {
  task_uid: string;
  sequence: number;
  merchant_name: string | null;
  estimated: TaskEstimatedTiming;
  actual: TaskActualTiming | null;
  comparison: TaskTimingComparison | null;
}

export interface RouteTaskTimings {
  route_id: string;
  driver_name: string | null;
  driver_uid: string;
  total_tasks: number;
  completed_tasks: number;
  total_actual_duration_min: number | null;
  total_estimated_duration_min: number;
  variance_min: number | null;
  variance_percent: number | null;
  tasks: TaskTimingDetail[];
}

/**
 * Get actual vs estimated task timings for a route
 * Fetches actual times from Core DB task_status table
 */
export async function getRouteTaskTimings(routeId: string): Promise<RouteTaskTimings> {
  const response = await api.get<ApiResponse<RouteTaskTimings>>(
    `/routes/${routeId}/task-timings`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch task timings');
  }
  
  return response.data.data;
}
